import * as THREE from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import { EXRLoader } from 'three/examples/jsm/loaders/EXRLoader.js';
import { CollisionSystem } from '../collision/CollisionSystem';
import type { EnvironmentConfig, EulerLike, Vector3Like } from '../sceneConfig';

interface LoadFbxAssetOptions {
  name: string;
  visible?: boolean;
  addToCollisionSystem?: boolean;
  scale?: number;
  position?: Vector3Like;
  rotation?: EulerLike;
  hideGeometry?: boolean;
  showEdgesInDev?: boolean;
  edgeColor?: number;
  doubleSided?: boolean;
}

interface EnvironmentManagerOptions {
  scene: THREE.Scene;
  fbxLoader: FBXLoader;
  collisionSystem: CollisionSystem;
  isDevMode: boolean;
  config: EnvironmentConfig;
  initialSkyboxUrl?: string;
  onCollisionDebugEdges?: (edges: THREE.Object3D[]) => void;
}

export class EnvironmentManager {
  private readonly scene: THREE.Scene;
  private readonly fbxLoader: FBXLoader;
  private readonly collisionSystem: CollisionSystem;
  private readonly isDevMode: boolean;
  private readonly config: EnvironmentConfig;
  private readonly onCollisionDebugEdges?: (edges: THREE.Object3D[]) => void;
  private readonly textureLoader: THREE.TextureLoader;
  private loadedTexture: THREE.Texture | null = null;
  private skyboxUrl: string | null;
  private skyboxIntensityOverride: number | null = null;
  private backgroundColor: number;
  private skyboxEnabled: boolean = false;

  constructor(options: EnvironmentManagerOptions) {
    this.scene = options.scene;
    this.fbxLoader = options.fbxLoader;
    this.collisionSystem = options.collisionSystem;
    this.isDevMode = options.isDevMode;
    this.config = options.config;
    this.onCollisionDebugEdges = options.onCollisionDebugEdges;
    this.textureLoader = new THREE.TextureLoader();
    this.skyboxUrl = options.initialSkyboxUrl ?? options.config.background.skybox?.url ?? null;
    this.backgroundColor = options.config.background.color;
  }

  addLighting(): void {
    this.addGlobalLight(this.scene);
    this.setupBackground();
  }

  async loadEnvironmentMeshes(): Promise<void> {
    await Promise.allSettled(
      this.config.collisionMeshes.map((mesh) =>
        this.loadFbxAsset(mesh.url, mesh)
      )
    );
  }

  createCollisionVolume(
    size: THREE.Vector3,
    center: THREE.Vector3,
    sourceObject: THREE.Object3D,
    options: { addMeshPosition?: boolean } = {}
  ): THREE.Mesh {
    const { addMeshPosition = false } = options;
    const inflatedSize = size
      .clone()
      .multiplyScalar(this.config.collisionVolume.inflateMultiplier);
    const collisionGeometry = new THREE.BoxGeometry(
      inflatedSize.x,
      inflatedSize.y,
      inflatedSize.z
    );
    const collisionMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    const collisionMesh = new THREE.Mesh(collisionGeometry, collisionMaterial);
    collisionMesh.visible = false;
    collisionMesh.position.copy(center);
    if (addMeshPosition) {
      collisionMesh.position.add(sourceObject.position);
    }
    collisionMesh.quaternion.copy(sourceObject.quaternion);
    this.scene.add(collisionMesh);
    this.collisionSystem.addCollider(collisionMesh);
    this.addCollisionDebugEdges(collisionMesh, collisionGeometry);
    return collisionMesh;
  }

  private async loadFbxAsset(url: string, options: LoadFbxAssetOptions): Promise<void> {
    const {
      name,
      visible = true,
      addToCollisionSystem = false,
      scale = 1,
      position,
      rotation,
      hideGeometry = false,
      showEdgesInDev = false,
      edgeColor = 0x00ff00,
      doubleSided = false,
    } = options;

    try {
      const object = await this.fbxLoader.loadAsync(url);
      object.name = name;

      if (position) {
        object.position.set(position.x, position.y, position.z);
      }

      if (scale !== 1) {
        object.scale.setScalar(scale);
      }

      if (rotation) {
        object.rotation.set(rotation.x, rotation.y, rotation.z);
      }

      object.traverse((child) => {
        if (!(child instanceof THREE.Mesh)) {
          return;
        }
        if (doubleSided) {
          const materials = Array.isArray(child.material) ? child.material : [child.material];
          materials.forEach((material) => {
            if (material) {
              material.side = THREE.DoubleSide;
            }
          });
        }
        if (hideGeometry) {
          child.visible = false;
        }
      });

      object.visible = hideGeometry ? false : visible;
      this.scene.add(object);
      if (addToCollisionSystem) {
        this.collisionSystem.addCollider(object);
      }
      if (this.isDevMode && showEdgesInDev) {
        this.addDebugEdgesForObject(object, edgeColor);
      }
    } catch (error) {
      console.error(`Failed to load FBX asset "${name}" from ${url}`, error);
    }
  }

  private addGlobalLight(targetScene: THREE.Scene): void {
    const { hemisphereLight, directionalLights } = this.config;
    const ambient = new THREE.HemisphereLight(
      hemisphereLight.skyColor,
      hemisphereLight.groundColor,
      hemisphereLight.intensity
    );
    const lightObjects = directionalLights.map((light) => {
      const directional = new THREE.DirectionalLight(light.color, light.intensity);
      directional.position.set(light.position.x, light.position.y, light.position.z);
      return directional;
    });
    targetScene.add(ambient, ...lightObjects);
  }

  /** Set up initial background - use background.color during preview phase */
  private setupBackground(): void {
    const { background } = this.config;
    this.scene.background = new THREE.Color(background.color);
  }

  /** Switch background to the main state (post-preview) - skybox or color */
  setMainBackground(): void {
    const { background } = this.config;
    const url = this.skyboxUrl || background.skybox?.url;
    if (url) {
      this.skyboxEnabled = true;
      this.loadSkyboxTexture(
        url,
        this.skyboxIntensityOverride ?? background.skybox?.intensity ?? 1.0,
        background.skybox?.blur ?? 0.0,
        background.skybox?.rotation ?? [0, 0, 0]
      );
    } else {
      this.skyboxEnabled = false;
      this.scene.background = new THREE.Color(background.color);
    }
  }

  /** Set background to a solid color */
  setBackgroundColor(color: number): void {
    this.backgroundColor = color;
    // Only apply if skybox is not currently active
    if (!(this.scene.background instanceof THREE.Texture)) {
      this.scene.background = new THREE.Color(color);
    }
  }

  /** Enable or disable skybox */
  setSkyboxEnabled(enabled: boolean): void {
    // Only act if state actually changed
    if (enabled === this.skyboxEnabled) {
      return;
    }
    this.skyboxEnabled = enabled;
    
    if (enabled && this.skyboxUrl) {
      // Load skybox texture using the dynamic URL
      const skybox = this.config.background.skybox;
      this.loadSkyboxTexture(
        this.skyboxUrl,
        skybox?.intensity ?? 1.0,
        skybox?.blur ?? 0.0,
        skybox?.rotation ?? [0, 0, 0]
      );
    } else {
      // Dispose texture and show background color
      if (this.loadedTexture) {
        this.loadedTexture.dispose();
        this.loadedTexture = null;
      }
      this.scene.background = new THREE.Color(this.backgroundColor);
    }
  }

  /** Set skybox URL and reload texture if skybox is active */
  setSkyboxUrl(url: string): void {
    const normalizedUrl = url || null;
    if (normalizedUrl === this.skyboxUrl) {
      return;
    }
    this.skyboxUrl = normalizedUrl;

    if (this.skyboxEnabled && this.skyboxUrl) {
      // Reload texture while preserving current scene settings
      this.loadSkyboxTexture(
        this.skyboxUrl,
        this.scene.backgroundIntensity,
        this.scene.backgroundBlurriness,
        [
          THREE.MathUtils.radToDeg(this.scene.backgroundRotation.x),
          THREE.MathUtils.radToDeg(this.scene.backgroundRotation.y),
          THREE.MathUtils.radToDeg(this.scene.backgroundRotation.z),
        ]
      );
    }
  }

  /** Set skybox intensity */
  setSkyboxIntensity(intensity: number): void {
    this.skyboxIntensityOverride = intensity;
    this.scene.backgroundIntensity = intensity;
  }

  /** Set skybox blur (0-1) */
  setSkyboxBlur(blur: number): void {
    this.scene.backgroundBlurriness = blur;
  }

  /** Set skybox rotation (degrees) */
  setSkyboxRotation(rotation: [number, number, number]): void {
    this.scene.backgroundRotation.set(
      THREE.MathUtils.degToRad(rotation[0]),
      THREE.MathUtils.degToRad(rotation[1]),
      THREE.MathUtils.degToRad(rotation[2])
    );
  }

  /** Load skybox texture with settings. Picks EXRLoader or TextureLoader based on file extension. */
  private loadSkyboxTexture(
    url: string,
    intensity: number,
    blur: number,
    rotation: [number, number, number]
  ): void {
    const applyTexture = (texture: THREE.Texture) => {
      if (this.loadedTexture) {
        this.loadedTexture.dispose();
      }
      texture.mapping = THREE.EquirectangularReflectionMapping;
      this.scene.background = texture;
      this.loadedTexture = texture;

      this.scene.backgroundIntensity = intensity;
      this.scene.backgroundBlurriness = blur;
      this.scene.backgroundRotation.set(
        THREE.MathUtils.degToRad(rotation[0]),
        THREE.MathUtils.degToRad(rotation[1]),
        THREE.MathUtils.degToRad(rotation[2])
      );
    };

    const onError = (error: unknown) => {
      console.error('Failed to load skybox texture:', error);
    };

    if (url.endsWith('.exr')) {
      new EXRLoader().load(url, applyTexture, undefined, onError);
    } else {
      this.textureLoader.load(url, applyTexture, undefined, onError);
    }
  }

  /** @deprecated Use loadSkyboxTexture instead */
  setBackgroundTexture(url: string): void {
    this.loadSkyboxTexture(url, 1.0, 0.0, [0, 0, 0]);
  }

  private addDebugEdgesForObject(object: THREE.Object3D, color: number): void {
    if (!this.isDevMode) {
      return;
    }
    object.updateMatrixWorld(true);
    object.traverse((child) => {
      if (!(child instanceof THREE.Mesh) || !child.geometry) {
        return;
      }
      const edgesGeometry = new THREE.EdgesGeometry(child.geometry);
      const edgesMaterial = new THREE.LineBasicMaterial({
        color,
        transparent: true,
        opacity: 0.9,
        depthWrite: false,
        depthTest: false,
      });
      const edges = new THREE.LineSegments(edgesGeometry, edgesMaterial);
      edges.matrixAutoUpdate = false;
      child.updateWorldMatrix(true, false);
      edges.matrix.copy(child.matrixWorld);
      edges.matrixWorld.copy(child.matrixWorld);
      edges.frustumCulled = false;
      edges.renderOrder = 10;
      this.pushCollisionEdges(edges);
      this.scene.add(edges);
    });
  }

  private addCollisionDebugEdges(
    collisionMesh: THREE.Mesh,
    geometry: THREE.BoxGeometry
  ): void {
    if (!this.isDevMode) {
      return;
    }
    const edgesGeometry = new THREE.EdgesGeometry(geometry);
    const edgesMaterial = new THREE.LineBasicMaterial({
      color: 0x00ff00,
      transparent: true,
      opacity: 0.9,
      depthTest: false,
      depthWrite: false,
    });
    const edges = new THREE.LineSegments(edgesGeometry, edgesMaterial);
    edges.matrixAutoUpdate = false;
    collisionMesh.updateWorldMatrix(true, false);
    edges.matrix.copy(collisionMesh.matrixWorld);
    edges.matrixWorld.copy(collisionMesh.matrixWorld);
    edges.frustumCulled = false;
    edges.renderOrder = 10;
    this.pushCollisionEdges(edges);
    this.scene.add(edges);
  }

  private pushCollisionEdges(edges: THREE.Object3D): void {
    this.onCollisionDebugEdges?.([edges]);
  }
}
